import React from "react";
import GenericButton from "../../../components/Buttons/GenericButton";
import { InputList } from "../../../components/Input/InputList";
import type { Country } from "../../../types/common";
import type { ComponentConfig } from "../types";
import { createRefinanceInputList } from "./refinanceInputList";
import type {
  ExtendedRefinancingDetails,
  HandleOnBlurProps,
  RefinancingDetails,
} from "./types";
import { validateOnBlur } from "./validation";

interface RefinancingDetailsProps {
  setNewRefinancingDetails: React.Dispatch<React.SetStateAction<any>>;
  setErrorMessages: React.Dispatch<React.SetStateAction<string>>;
  newRefinancingDetails: any;
  internalRedemption: any;
  updateAmountSum: (newAmountSum: number) => void;
  refinanceDetailsId: React.MutableRefObject<number>;
  noErrorMessage: string;
  country?: Country;
}

export const RefinanceDetailsTab = ({
  setErrorMessages,
  updateAmountSum,
  internalRedemption,
  newRefinancingDetails,
  refinanceDetailsId,
  setNewRefinancingDetails,
  noErrorMessage,
  country,
}: RefinancingDetailsProps) => {
  let inputOrder = 0;
  let infoConfig: ComponentConfig[] = [];

  const handleDelete = (idToDelete: string | undefined) => {
    setErrorMessages(noErrorMessage);
    setNewRefinancingDetails((prevDetails: any) =>
      prevDetails?.filter((detail: any) => {
        return detail.id !== idToDelete;
      }),
    );
    infoConfig = removeComponent(idToDelete, infoConfig);
  };

  const removeComponent = (
    idToDelete: string | undefined,
    infoConfig: ComponentConfig[],
  ) => {
    const newInfoConfig = infoConfig.filter((config) => {
      if (config.component && React.isValidElement(config.component)) {
        if (config.component.type !== InputList) {
          return true;
        }
        const props: any = config.component.props;
        if (
          Array.isArray(props.infoLists) &&
          props.infoLists[0]?.id !== idToDelete
        ) {
          return true;
        }
      }
      return false;
    });
    return newInfoConfig;
  };

  if (newRefinancingDetails?.length === 0) {
    refinanceDetailsId.current++;
    const emptyInputFields: RefinancingDetails = {
      type: "loan",
      accountNumber: "",
      bankName: "",
      amount: 0,
      paymentReference: "",
      description: "",
      monthlyAmount: 0,
      isFromUc: false,
    };
    newRefinancingDetails.push({
      ...emptyInputFields,
      id: refinanceDetailsId.current.toString(),
      order: 0,
    });
  }

  const handleOnBlur = (params: HandleOnBlurProps) => {
    const { key, e, id, setErrorMessages, setNewRefinancingDetails } = params;
    const value = validateOnBlur(key, e, setErrorMessages, country);
    setNewRefinancingDetails((prevDetails: ExtendedRefinancingDetails[]) => {
      const updatedDetails = prevDetails?.map((detail) => {
        if (detail.id !== id) {
          return detail;
        }
        return {
          ...detail,
          [key]: value,
        };
      });
      return updatedDetails;
    });
  };

  const addNewInput = () => {
    refinanceDetailsId.current++;
    infoConfig = handleClickAddNewButton(
      refinanceDetailsId.current,
      setNewRefinancingDetails,
      infoConfig,
      handleDelete,
      setErrorMessages,
    );
  };

  const handleClickAddNewButton = (
    refinanceDetailsId: number,
    setNewRefinancingDetails: (
      value: React.SetStateAction<ExtendedRefinancingDetails[]>,
    ) => void,
    infoConfig: ComponentConfig[],
    handleDelete: (id: string) => void,
    setErrorMessages: (value: React.SetStateAction<string>) => void,
  ) => {
    const newObject: ExtendedRefinancingDetails = {
      type: "loan",
      accountNumber: "",
      bankName: "",
      id: refinanceDetailsId.toString(),
      amount: 0,
      paymentReference: "",
      order: refinanceDetailsId,
      description: "",
      monthlyAmount: 0,
      isFromUc: false,
    };
    const { id, order, ...visibleDetails } = newObject;
    const context = visibleDetails;

    setNewRefinancingDetails((prevDetails) => [
      ...(prevDetails ?? []),
      newObject,
    ]);
    const newInfoConfig: ComponentConfig[] = [
      ...infoConfig,
      {
        component: (
          <InputList
            deletable={true}
            onClick={() => handleDelete(id)}
            title={"Refinance details"}
            infoLists={[
              {
                title: "",
                id: id,
                context: context,
                onBlur: (key, e) =>
                  handleOnBlur({
                    key,
                    e,
                    id,
                    setErrorMessages,
                    setNewRefinancingDetails,
                  }),
              },
            ]}
          />
        ),
        order: order,
      },
    ];
    return newInfoConfig;
  };

  const refinanceInputs = createRefinanceInputList({
    updateAmountSum,
    handleDelete,
    handleOnBlur,
    inputOrder,
    newRefinancingDetails,
    setErrorMessages,
    setNewRefinancingDetails,
  });
  infoConfig = [...infoConfig, ...refinanceInputs];
  inputOrder += refinanceInputs.length;

  if (internalRedemption) {
    infoConfig.push({
      component: (
        <InputList
          deletable={false}
          onClick={() => {}}
          title={"Internal redemption"}
          infoLists={[
            {
              title: "",
              context: internalRedemption,
              onBlur: (key, e) => {},
              disabled: true,
            },
          ]}
        />
      ),
      order: inputOrder + 1,
    });
    inputOrder++;
  }

  infoConfig.push({
    component: <GenericButton onClick={addNewInput} type="AddNewButton" />,
    order: inputOrder + 1,
  });
  return infoConfig;
};
